import { useEffect, useState, type FormEvent, type KeyboardEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import {
  api,
  ApiError,
  type Adjudicacion,
  type Distribucion,
  type FiduciantePublico,
  type Unidad,
  type VistaPreviaAdjudicacion,
} from "@/lib/api";
import {
  aPayloadAdjudicacion,
  diferenciaEscalada,
  formAdjudicacion,
  opcionesClaseB,
  opcionesContrapartes,
  opcionesUnidades,
  parseClave,
  queda,
  textoAtrasado,
  textoDiferencia,
  textoQueda,
  type FormAdjudicacion,
  type PayloadAdjudicacion,
} from "@/lib/adjudicaciones";
import { aEditable, aEscalado, hoyIso } from "@/lib/participacion";

type Dialogo =
  | { tipo: "nueva" }
  | { tipo: "deshacer"; adjudicacion: Adjudicacion }
  | null;

/** La línea de quién cede o recibe que se está armando ("id-clase" + %). */
interface Nueva {
  clave: string | null;
  porcentaje: string;
}
const NUEVA_VACIA: Nueva = { clave: null, porcentaje: "" };

/** Lógica de Adjudicaciones (sin JSX). Admin y contador leen; sólo el admin adjudica y deshace. */
export function useAdjudicacionesTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}`;
  const esAdmin = membresia.rol === "admin";
  const adjudicaciones = useApiResource<Adjudicacion[]>(
    `${base}/adjudicaciones`,
  );
  // Para el formulario (sólo el admin): la distribución vigente, las unidades y los fiduciantes.
  const distribuciones = useApiResource<Distribucion[]>(
    esAdmin ? `${base}/distribuciones` : null,
  );
  const unidades = useApiResource<Unidad[]>(
    esAdmin ? `${base}/unidades` : null,
  );
  const fiduciantes = useApiResource<FiduciantePublico[]>(
    esAdmin ? `${base}/fiduciantes` : null,
  );

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormAdjudicacion>(
    formAdjudicacion(hoyIso()),
  );
  const [nueva, setNueva] = useState<Nueva>(NUEVA_VACIA);
  const [vista, setVista] = useState<VistaPreviaAdjudicacion | null>(null);
  const [errorVista, setErrorVista] = useState<string | null>(null);

  const accionCrear = useAsyncAction((datos: PayloadAdjudicacion) =>
    api.post<Adjudicacion>(`${base}/adjudicaciones`, datos),
  );
  const accionDeshacer = useAsyncAction((id: number) =>
    api.delete<void>(`${base}/adjudicaciones/${id}`).then(() => true),
  );

  // La vista previa se pide cada vez que cambian el fiduciante, las unidades o la fecha (con los tres completos).
  const listo =
    dialogo?.tipo === "nueva" &&
    form.fiduciante_id !== null &&
    form.unidad_ids.length > 0 &&
    form.fecha !== "";
  const claveVista = listo
    ? JSON.stringify([form.fiduciante_id, form.unidad_ids, form.fecha])
    : null;
  useEffect(() => {
    setVista(null);
    setErrorVista(null);
    if (claveVista === null) return;
    const [fiduciante_id, unidad_ids, fecha] = JSON.parse(claveVista) as [
      number,
      number[],
      string,
    ];
    let vigente = true;
    api
      .post<VistaPreviaAdjudicacion>(`${base}/adjudicaciones/vista-previa`, {
        fiduciante_id,
        unidad_ids,
        fecha,
      })
      .then((v) => vigente && setVista(v))
      .catch(
        (e: unknown) =>
          vigente &&
          setErrorVista(
            e instanceof ApiError ? e.message : "No se pudo calcular",
          ),
      );
    return () => {
      vigente = false;
    };
  }, [base, claveVista]);

  const vigente = (distribuciones.data ?? []).find((d) => d.vigente) ?? null;
  const listaUnidades = unidades.data ?? [];
  const listaFiduciantes = fiduciantes.data ?? [];
  const nombreDe = (id: number | null) =>
    listaFiduciantes.find((f) => f.id === id)?.nombre ?? "";
  const diferencia = vista ? diferenciaEscalada(vista) : 0;
  const resto = vista ? queda(vista, form.contrapartes) : null;
  const restoConNueva =
    vista && nueva.clave !== null && nueva.porcentaje.trim() !== ""
      ? queda(vista, [
          ...form.contrapartes,
          {
            ...parseClave(nueva.clave),
            nombre: "",
            porcentaje: nueva.porcentaje,
          },
        ])
      : resto;

  const recargar = () => {
    adjudicaciones.recargar();
    distribuciones.recargar();
    unidades.recargar();
  };
  const cerrar = () => setDialogo(null);

  const abrirNueva = () => {
    accionCrear.limpiarError();
    setForm(formAdjudicacion(hoyIso()));
    setNueva(NUEVA_VACIA);
    setDialogo({ tipo: "nueva" });
  };
  const elegirFiduciante = (valor: string) =>
    setForm((f) => ({ ...f, fiduciante_id: Number(valor), contrapartes: [] }));
  const soltarFiduciante = () =>
    setForm((f) => ({ ...f, fiduciante_id: null, contrapartes: [] }));
  const agregarUnidad = (valor: string) =>
    setForm((f) => ({
      ...f,
      unidad_ids: [...f.unidad_ids, Number(valor)],
      contrapartes: [],
    }));
  const quitarUnidad = (id: number) =>
    setForm((f) => ({
      ...f,
      unidad_ids: f.unidad_ids.filter((u) => u !== id),
      contrapartes: [],
    }));
  const cambiarFecha = (fecha: string) => setForm((f) => ({ ...f, fecha }));
  const cambiarNota = (nota: string) => setForm((f) => ({ ...f, nota }));

  const opcionesNueva =
    vista && diferencia !== 0
      ? opcionesContrapartes(vista, listaFiduciantes, form)
      : [];
  const etiquetaClave = (clave: string) =>
    opcionesNueva.find((o) => o.valor === clave)?.etiqueta ?? "";
  const elegirContraparte = (clave: string) =>
    setNueva((n) => ({ ...n, clave }));
  const soltarContraparte = () => setNueva(NUEVA_VACIA);
  const cambiarPorcentaje = (porcentaje: string) =>
    setNueva((n) => ({ ...n, porcentaje }));
  const usarLoQueQueda = () =>
    setNueva((n) => ({ ...n, porcentaje: aEditable(resto ?? 0) }));
  const puedeAgregar =
    nueva.clave !== null && (aEscalado(nueva.porcentaje) ?? 0) > 0;
  const agregarContraparte = () => {
    if (!puedeAgregar || nueva.clave === null) return;
    const { fiduciante_id, clase } = parseClave(nueva.clave);
    setForm((f) => ({
      ...f,
      contrapartes: [
        ...f.contrapartes,
        {
          fiduciante_id,
          clase,
          nombre: nombreDe(fiduciante_id),
          porcentaje: nueva.porcentaje,
        },
      ],
    }));
    setNueva(NUEVA_VACIA);
  };
  /** Enter en el % agrega la línea en vez de mandar el formulario. */
  const teclaEnNueva = (e: KeyboardEvent) => {
    if (
      e.key !== "Enter" ||
      (e.target as HTMLElement).getAttribute("role") === "combobox"
    )
      return;
    e.preventDefault();
    agregarContraparte();
  };
  const quitarContraparte = (indice: number) =>
    setForm((f) => ({
      ...f,
      contrapartes: f.contrapartes.filter((_, i) => i !== indice),
    }));

  // Una línea armada sin tocar Agregar también se guarda (lo que se ve es lo que se guarda).
  const formFinal: FormAdjudicacion =
    puedeAgregar && nueva.clave !== null
      ? {
          ...form,
          contrapartes: [
            ...form.contrapartes,
            {
              ...parseClave(nueva.clave),
              nombre: "",
              porcentaje: nueva.porcentaje,
            },
          ],
        }
      : form;
  const puedeAdjudicar =
    vista !== null &&
    (diferencia === 0 || restoConNueva === 0) &&
    !accionCrear.loading;

  const adjudicar = async (e: FormEvent) => {
    e.preventDefault();
    if (!puedeAdjudicar) return;
    if (await accionCrear.run(aPayloadAdjudicacion(formFinal))) {
      cerrar();
      recargar();
    }
  };

  const abrirDeshacer = (a: Adjudicacion) => {
    accionDeshacer.limpiarError();
    setDialogo({ tipo: "deshacer", adjudicacion: a });
  };
  const confirmarDeshacer = async () => {
    if (dialogo?.tipo !== "deshacer") return;
    if (await accionDeshacer.run(dialogo.adjudicacion.id)) {
      cerrar();
      recargar();
    }
  };

  const nombreAdjudicatario = nombreDe(form.fiduciante_id);
  return {
    cargando: !adjudicaciones.data && adjudicaciones.loading,
    errorCarga: adjudicaciones.data
      ? null
      : (adjudicaciones.error?.message ?? null),
    reintentar: recargar,
    adjudicaciones: adjudicaciones.data ?? [],
    esAdmin,
    dialogo,
    cerrar,
    abrirNueva,
    form,
    opcionesFiduciantes: opcionesClaseB(vigente),
    sinClaseB: vigente !== null && opcionesClaseB(vigente).length === 0,
    sinVigente: distribuciones.data !== undefined && vigente === null,
    nombreAdjudicatario,
    elegirFiduciante,
    soltarFiduciante,
    opcionesUnidades: opcionesUnidades(listaUnidades, form.unidad_ids),
    unidadesElegidas: form.unidad_ids
      .map((id) => listaUnidades.find((u) => u.id === id))
      .filter((u): u is Unidad => u !== undefined),
    agregarUnidad,
    quitarUnidad,
    cambiarFecha,
    cambiarNota,
    vista,
    errorVista,
    calculando: listo && vista === null && errorVista === null,
    textoDiferencia: vista ? textoDiferencia(vista) : null,
    textoAtrasado: vista
      ? textoAtrasado(vista, nombreAdjudicatario, form.fecha)
      : null,
    hayDiferencia: diferencia !== 0,
    leFalta: diferencia > 0,
    opcionesNueva,
    nueva,
    etiquetaNueva: nueva.clave === null ? "" : etiquetaClave(nueva.clave),
    elegirContraparte,
    soltarContraparte,
    cambiarPorcentaje,
    usarLoQueQueda: resto !== null && resto > 0 ? usarLoQueQueda : null,
    puedeAgregar,
    agregarContraparte,
    teclaEnNueva,
    quitarContraparte,
    textoQueda: textoQueda(restoConNueva),
    repartidoTodo: restoConNueva === 0,
    puedeAdjudicar,
    adjudicar,
    adjudicando: accionCrear.loading,
    errorAdjudicar: accionCrear.error,
    abrirDeshacer,
    confirmarDeshacer,
    deshaciendo: accionDeshacer.loading,
    errorDeshacer: accionDeshacer.error,
  };
}
